import { supabase } from '@/lib/supabase';
import { unwrap, ServiceError } from '@/data/serviceError';
import type { PhoneCallWithRelations } from '../types';

const SELECT_WITH_RELATIONS =
  '*, contacts(id, name), leads(id, contact_name, work_type), projects(id, name, project_number, address, contact_person_name)';

/** Hoe lang een rinkelend gesprek meetelt voor de screen-pop. */
const RINGING_WINDOW_MS = 2 * 60 * 1000;

/**
 * Data-access voor het `telephony`-domein. De ENIGE plek die met
 * `supabase.from('phone_calls')` praat. Componenten gebruiken `usePhoneCalls*`.
 * De tabel wordt gevuld door de `voys-webhook` edge function; vanuit de UI
 * zijn alleen de koppelvelden (contact_id/lead_id) schrijfbaar.
 */
export const phoneCallsService = {
  /** Alle recente gesprekken voor het belregister (nieuwste eerst). */
  async listRegister(): Promise<PhoneCallWithRelations[]> {
    return unwrap(
      await supabase
        .from('phone_calls')
        .select(SELECT_WITH_RELATIONS)
        .order('started_at', { ascending: false, nullsFirst: false })
        .limit(300),
      'Belregister laden',
    ) as unknown as PhoneCallWithRelations[];
  },

  /**
   * Rinkelende inkomende gesprekken van de afgelopen 2 minuten (oudste eerst),
   * voor de screen-pop. Gesprekken die al 'ended' of 'in-progress' zijn vallen
   * buiten het statusfilter en poppen dus niet meer.
   */
  async listRinging(): Promise<PhoneCallWithRelations[]> {
    const cutoff = new Date(Date.now() - RINGING_WINDOW_MS).toISOString();
    return unwrap(
      await supabase
        .from('phone_calls')
        .select(SELECT_WITH_RELATIONS)
        .eq('direction', 'inbound')
        .in('status', ['created', 'ringing'])
        // started_at binnen het venster; net-aangemaakte rijen zonder
        // started_at tellen mee op created_at.
        .or(`started_at.gte.${cutoff},and(started_at.is.null,created_at.gte.${cutoff})`)
        .order('created_at', { ascending: true })
        .limit(10),
      'Rinkelende gesprekken laden',
    ) as unknown as PhoneCallWithRelations[];
  },

  async listForLead(leadId: string): Promise<PhoneCallWithRelations[]> {
    return unwrap(
      await supabase
        .from('phone_calls')
        .select(SELECT_WITH_RELATIONS)
        .eq('lead_id', leadId)
        .order('started_at', { ascending: false, nullsFirst: false })
        .limit(300),
      'Gesprekken van lead laden',
    ) as unknown as PhoneCallWithRelations[];
  },

  async listForContact(contactId: string): Promise<PhoneCallWithRelations[]> {
    return unwrap(
      await supabase
        .from('phone_calls')
        .select(SELECT_WITH_RELATIONS)
        .eq('contact_id', contactId)
        .order('started_at', { ascending: false, nullsFirst: false })
        .limit(300),
      'Gesprekken van contact laden',
    ) as unknown as PhoneCallWithRelations[];
  },

  async listForProject(projectId: string): Promise<PhoneCallWithRelations[]> {
    return unwrap(
      await supabase
        .from('phone_calls')
        .select(SELECT_WITH_RELATIONS)
        .eq('project_id', projectId)
        .order('started_at', { ascending: false, nullsFirst: false })
        .limit(300),
      'Gesprekken van project laden',
    ) as unknown as PhoneCallWithRelations[];
  },

  /** Koppel een gesprek aan een bestaand contact. */
  async linkToContact(callId: string, contactId: string): Promise<void> {
    const { error } = await supabase
      .from('phone_calls')
      .update({ contact_id: contactId })
      .eq('id', callId);
    if (error) throw new ServiceError(`Gesprek koppelen aan contact: ${error.message}`, error.code);
  },

  /** Koppel een gesprek aan een bestaande lead. */
  async linkToLead(callId: string, leadId: string): Promise<void> {
    const { error } = await supabase
      .from('phone_calls')
      .update({ lead_id: leadId })
      .eq('id', callId);
    if (error) throw new ServiceError(`Gesprek koppelen aan lead: ${error.message}`, error.code);
  },
};
